import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { 
  Plus, 
  Download, 
  FileSpreadsheet, 
  Calendar,
  Trash2,
  HelpCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Edit3,
  X,
  Check,
  AlertCircle,
  Building,
  Home as HomeIcon,
  Target,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import axios from 'axios';
import Cookies from 'js-cookie';
import PnLAICoach from '../PnLAICoach';
import API_BASE_URL from '../../config/api';

const PnLPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getToken } = useClerkAuth();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const [pnlSummary, setPnlSummary] = useState(null);
  const [activeDeals, setActiveDeals] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Filter active deals by the selected month so the DD Tracker
  // matches Total Income / Total Expenses scoping on the P&L page.
  const filteredActiveDeals = useMemo(() => {
    if (!Array.isArray(activeDeals) || !selectedMonth) return [];
    return activeDeals.filter(
      (deal) => deal.closing_date && deal.closing_date.startsWith(selectedMonth)
    );
  }, [activeDeals, selectedMonth]);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [error, setError] = useState(null);
  const [capProgress, setCapProgress] = useState(null);
  const [capLoading, setCapLoading] = useState(true);
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { type: 'deal'|'expense', id: string, field: string }
  const [editValue, setEditValue] = useState('');
  
  // Edit expense modal state
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Budget editing state
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetInputValue, setBudgetInputValue] = useState('');
  
  // AI Coach state
  const [showAICoach, setShowAICoach] = useState(false);

  // Get backend URL
  const backendUrl = API_BASE_URL;

  // Deal form state - initialize dates with today's date and sensible defaults
  const today = new Date().toISOString().slice(0, 10);
  const [newDeal, setNewDeal] = useState({
    house_address: '',
    amount_sold_for: '',
    commission_percent: '3',
    split_percent: '100',
    team_brokerage_split_percent: '0',
    lead_source: '',
    contract_signed: today,
    due_diligence_start: today,
    due_diligence_over: today,
    closing_date: today
  });

  // Edit deal state
  const [editingDeal, setEditingDeal] = useState(null);
  const [showEditDeal, setShowEditDeal] = useState(false);

  // Expense form state
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    budget: 0,
    amount: '',
    description: '',
    recurring: false
  });

  useEffect(() => {
    loadInitialData();
    loadActiveDeals();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadPnLData();
    }
  }, [selectedMonth]);

  const loadInitialData = async () => {
    try {
      // Get auth token for API calls that require authentication
      const token = await getToken();
      const authConfig = {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      };
      
      // Load categories and lead sources with auth
      const [categoriesResponse, leadSourcesResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/pnl/categories`, authConfig).catch((err) => {
          console.warn('[PnLPanel] Failed to load categories:', err.response?.status, err.message);
          return { data: [] };
        }),
        axios.get(`${backendUrl}/api/pnl/lead-sources`, authConfig).catch((err) => {
          console.warn('[PnLPanel] Failed to load lead sources:', err.response?.status, err.message);
          return { data: [] };
        })
      ]);

      setExpenseCategories(categoriesResponse.data);
      setLeadSources(leadSourcesResponse.data);
      
      console.log('[PnLPanel] Loaded lead sources:', leadSourcesResponse.data?.length || 0);
      
      // Load cap progress data
      await loadCapProgress();
      
      // Load P&L data for current month
      await loadPnLData();
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setError('Failed to load initial data');
    }
  };

  const loadPnLData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get auth token
      const token = await getToken();
      
      const response = await axios.get(`${backendUrl}/api/pnl/summary`, {
        params: { month: selectedMonth },
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      setPnlSummary(response.data);
    } catch (error) {
      console.error('Failed to load P&L data:', error);
      if (error.response?.status === 402) {
        setError('P&L Tracker requires a Pro plan. Please upgrade to access this feature.');
      } else if (error.response?.status === 401) {
        setError('Authentication required');
      } else {
        setError('Failed to load P&L data');
      }
      setPnlSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadActiveDeals = async () => {
    try {
      // Get auth token
      const token = await getToken();
      
      const response = await axios.get(`${backendUrl}/api/pnl/active-deals`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setActiveDeals(response.data || []);
    } catch (error) {
      console.error('Failed to load active deals:', error);
      setActiveDeals([]);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Handle add deal
  const handleAddDeal = async (e) => {
    e.preventDefault();
    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        console.error('[PnLPanel] No auth token available');
        setError('Authentication required. Please sign in again.');
        return;
      }

      // Validate required fields
      if (!newDeal.house_address || !newDeal.amount_sold_for || !newDeal.lead_source) {
        setError('Please fill in all required fields: Address, Amount, and Lead Source');
        return;
      }

      const dealData = {
        house_address: newDeal.house_address,
        amount_sold_for: parseFloat(newDeal.amount_sold_for.replace(/,/g, '')) || 0,
        commission_percent: parseFloat(newDeal.commission_percent) || 3,
        split_percent: parseFloat(newDeal.split_percent) || 100,
        team_brokerage_split_percent: parseFloat(newDeal.team_brokerage_split_percent) || 0,
        lead_source: newDeal.lead_source,
        contract_signed: newDeal.contract_signed,
        due_diligence_start: newDeal.due_diligence_start,
        due_diligence_over: newDeal.due_diligence_over,
        closing_date: newDeal.closing_date
      };

      console.log('[PnLPanel] Submitting deal:', dealData);

      const response = await axios.post(`${backendUrl}/api/pnl/deals`, dealData, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[PnLPanel] Deal added successfully:', response.data);
      
      // Reset form with sensible defaults
      const resetDate = new Date().toISOString().slice(0, 10);
      setNewDeal({
        house_address: '',
        amount_sold_for: '',
        commission_percent: '3',
        split_percent: '100',
        team_brokerage_split_percent: '0',
        lead_source: '',
        contract_signed: resetDate,
        due_diligence_start: resetDate,
        due_diligence_over: resetDate,
        closing_date: resetDate
      });
      setShowAddDeal(false);
      setError(null);
      
      // Reload data
      await loadPnLData();
      await loadActiveDeals(); // Refresh active deals
    } catch (error) {
      console.error('[PnLPanel] Failed to add deal:', error);
      console.error('[PnLPanel] Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Authentication required. Please sign in again.');
      } else if (error.response?.status === 402) {
        setError('PRO subscription required to add deals.');
      } else if (error.response?.status === 422) {
        setError('Invalid data. Please check all fields are filled correctly.');
      } else {
        setError(error.response?.data?.detail || 'Failed to add deal. Please try again.');
      }
    }
  };
  // Handle opening edit deal modal
  const handleEditDeal = (deal) => {
    setEditingDeal({
      id: deal.id,
      house_address: deal.house_address,
      amount_sold_for: deal.amount_sold_for.toString(),
      commission_percent: deal.commission_percent.toString(),
      split_percent: deal.split_percent.toString(),
      team_brokerage_split_percent: deal.team_brokerage_split_percent.toString(),
      lead_source: deal.lead_source,
      contract_signed: deal.contract_signed || '',
      due_diligence_start: deal.due_diligence_start || '',
      due_diligence_over: deal.due_diligence_over || '',
      closing_date: deal.closing_date
    });
    setShowEditDeal(true);
  };

  // Handle update deal form submission
  const handleUpdateDeal = async (e) => {
    e.preventDefault();
    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      await axios.patch(`${backendUrl}/api/pnl/deals/${editingDeal.id}`, editingDeal, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setEditingDeal(null);
      setShowEditDeal(false);
      await loadPnLData();
      await loadActiveDeals(); // Refresh active deals
    } catch (error) {
      console.error('Failed to update deal:', error);
      if (error.response?.status === 401) {
        setError('Authentication required');
      } else if (error.response?.status === 402) {
        setError('P&L Tracker requires a Pro plan. Please upgrade to access this feature.');
      } else {
        setError('Failed to update deal');
      }
    }
  };

  // Handle add expense
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      const expenseData = {
        ...newExpense,
        amount: parseFloat(newExpense.amount) || 0,
        budget: parseFloat(newExpense.budget) || 0
      };

      await axios.post(`${backendUrl}/api/pnl/expenses`, expenseData, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Reset form
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        category: '',
        budget: 0,
        amount: '',
        description: '',
        recurring: false
      });
      setShowAddExpense(false);
      
      // Reload data
      await loadPnLData();
    } catch (error) {
      console.error('Failed to add expense:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please sign in again.');
      } else {
        setError(error.response?.data?.detail || 'Failed to add expense');
      }
    }
  };

  // Delete deal
  const deleteDeal = async (dealId) => {
    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      await axios.delete(`${backendUrl}/api/pnl/deals/${dealId}`, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadPnLData();
      await loadActiveDeals(); // Refresh active deals
    } catch (error) {
      console.error('Failed to delete deal:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please sign in again.');
      } else {
        setError(error.response?.data?.detail || 'Failed to delete deal');
      }
    }
  };

  // Delete expense
  const deleteExpense = async (expenseId) => {
    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      await axios.delete(`${backendUrl}/api/pnl/expenses/${expenseId}`, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadPnLData();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please sign in again.');
      } else {
        setError(error.response?.data?.detail || 'Failed to delete expense');
      }
    }
  };

  // Handle opening edit expense modal
  const handleEditExpense = (expense) => {
    setEditingExpense({
      id: expense.id,
      date: expense.date || '',
      category: expense.category || '',
      description: expense.description || '',
      amount: expense.amount?.toString() || '',
      budget: expense.budget?.toString() || '0'
    });
    setShowEditExpense(true);
  };

  // Handle update expense form submission
  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      const updateData = {
        date: editingExpense.date,
        category: editingExpense.category,
        description: editingExpense.description,
        amount: parseFloat(editingExpense.amount) || 0,
        budget: parseFloat(editingExpense.budget) || 0
      };

      await axios.patch(`${backendUrl}/api/pnl/expenses/${editingExpense.id}`, updateData, {
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEditingExpense(null);
      setShowEditExpense(false);
      await loadPnLData();
    } catch (error) {
      console.error('Failed to update expense:', error);
      if (error.response?.status === 401) {
        setError('Authentication required');
      } else {
        setError('Failed to update expense');
      }
    }
  };

  // Handle setting a budget for a category
  const handleSetBudget = async (category) => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      const budgetAmount = parseFloat(budgetInputValue) || 0;
      if (budgetAmount <= 0) {
        setError('Please enter a valid budget amount');
        return;
      }

      await axios.post(`${backendUrl}/api/pnl/budgets?month=${selectedMonth}`, {
        category: category,
        monthly_budget: budgetAmount
      }, {
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setEditingBudget(null);
      setBudgetInputValue('');
      await loadPnLData();
    } catch (error) {
      console.error('Failed to set budget:', error);
      setError('Failed to set budget');
    }
  };

  // Format number with commas for display in Add Deal
  const formatAmountWithCommas = (value) => {
    if (!value) return '';
    const numericValue = value.toString().replace(/[^0-9]/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Handle amount change with formatting for Add Deal
  const handleDealAmountChange = (e) => {
    const formattedValue = formatAmountWithCommas(e.target.value);
    setNewDeal(prev => ({ ...prev, amount_sold_for: formattedValue }));
  };

  // Generate CSV export for month or year
  const handleExportCSV = async (period = 'month') => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      // Get year from selected month
      const [year, month] = selectedMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long' });

      let csvContent = '';
      let filename = '';

      if (period === 'month') {
        // Use current loaded data for single month export
        const deals = pnlSummary?.deals || [];
        const expenses = pnlSummary?.expenses || [];
        const totalIncome = pnlSummary?.total_income || 0;
        const totalExpenses = pnlSummary?.total_expenses || 0;
        const netIncome = pnlSummary?.net_income || 0;

        filename = `PnL_${monthName}_${year}.csv`;
        
        // Section 1: Income - Closed Deals
        csvContent += 'Income - Closed Deals\n';
        csvContent += '"Property Address","Sale Price","Commission %","Split %","Team/Brokerage Split %","Cap Amount","Final Income","Lead Source","Closing Date"\n';
        
        deals.forEach(deal => {
          const address = (deal.house_address || '').replace(/"/g, '""');
          csvContent += `"${address}","${formatCurrency(deal.amount_sold_for)}","${deal.commission_percent}%","${deal.split_percent}%","${deal.team_brokerage_split_percent}%","${formatCurrency(deal.cap_amount || 0)}","${formatCurrency(deal.final_income)}","${deal.lead_source || ''}","${new Date(deal.closing_date).toLocaleDateString()}"\n`;
        });
        
        csvContent += `\n"Total Income:","${formatCurrency(totalIncome)}"\n\n`;
        
        // Section 2: Expenses - Business Costs
        csvContent += 'Expenses - Business Costs\n';
        csvContent += '"Date","Category","Description","Amount"\n';
        
        expenses.forEach(expense => {
          const description = (expense.description || '').replace(/"/g, '""');
          csvContent += `"${new Date(expense.date).toLocaleDateString()}","${expense.category}","${description}","${formatCurrency(expense.amount)}"\n`;
        });
        
        csvContent += `\n"Total Expenses:","${formatCurrency(totalExpenses)}"\n\n`;
        
        // Section 3: Summary
        csvContent += 'Monthly P&L Summary\n';
        csvContent += `"Total Income:","${formatCurrency(totalIncome)}"\n`;
        csvContent += `"Total Expenses:","${formatCurrency(totalExpenses)}"\n`;
        csvContent += `"Net Income:","${formatCurrency(netIncome)}"\n`;
        
      } else {
        // YEAR EXPORT: Fetch data for all 12 months
        filename = `PnL_Annual_${year}.csv`;
        
        // Track annual totals
        let annualIncome = 0;
        let annualExpenses = 0;
        let allDeals = [];
        let allExpenses = [];
        const monthlyData = [];

        // Fetch data for each month of the selected year
        for (let m = 1; m <= 12; m++) {
          const monthKey = `${year}-${String(m).padStart(2, '0')}`;
          const monthLabel = new Date(parseInt(year), m - 1).toLocaleString('default', { month: 'long' });
          
          try {
            const response = await axios.get(`${backendUrl}/api/pnl/summary`, {
              params: { month: monthKey },
              withCredentials: true,
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const monthDeals = response.data?.deals || [];
            const monthExpenses = response.data?.expenses || [];
            const monthIncome = response.data?.total_income || 0;
            const monthExpenseTotal = response.data?.total_expenses || 0;
            
            monthlyData.push({
              month: monthLabel,
              monthKey,
              deals: monthDeals,
              expenses: monthExpenses,
              totalIncome: monthIncome,
              totalExpenses: monthExpenseTotal,
              netIncome: monthIncome - monthExpenseTotal
            });
            
            annualIncome += monthIncome;
            annualExpenses += monthExpenseTotal;
            allDeals = [...allDeals, ...monthDeals];
            allExpenses = [...allExpenses, ...monthExpenses];
            
          } catch (err) {
            // Month may have no data, continue
            monthlyData.push({
              month: monthLabel,
              monthKey,
              deals: [],
              expenses: [],
              totalIncome: 0,
              totalExpenses: 0,
              netIncome: 0
            });
          }
        }

        // Build CSV content
        csvContent += `Annual P&L Report - ${year}\n`;
        csvContent += `Generated: ${new Date().toLocaleDateString()}\n\n`;

        // Section 1: All Income - Closed Deals (grouped by month)
        csvContent += '=== INCOME - CLOSED DEALS ===\n\n';
        
        monthlyData.forEach(monthData => {
          if (monthData.deals.length > 0) {
            csvContent += `${monthData.month} ${year}\n`;
            csvContent += '"Property Address","Sale Price","Commission %","Split %","Team/Brokerage Split %","Cap Amount","Final Income","Lead Source","Closing Date"\n';
            
            monthData.deals.forEach(deal => {
              const address = (deal.house_address || '').replace(/"/g, '""');
              csvContent += `"${address}","${formatCurrency(deal.amount_sold_for)}","${deal.commission_percent}%","${deal.split_percent}%","${deal.team_brokerage_split_percent}%","${formatCurrency(deal.cap_amount || 0)}","${formatCurrency(deal.final_income)}","${deal.lead_source || ''}","${new Date(deal.closing_date).toLocaleDateString()}"\n`;
            });
            
            csvContent += `"${monthData.month} Total Income:","${formatCurrency(monthData.totalIncome)}"\n\n`;
          }
        });

        csvContent += `"ANNUAL TOTAL INCOME:","${formatCurrency(annualIncome)}"\n\n`;

        // Section 2: All Expenses - Business Costs (grouped by month)
        csvContent += '=== EXPENSES - BUSINESS COSTS ===\n\n';
        
        monthlyData.forEach(monthData => {
          if (monthData.expenses.length > 0) {
            csvContent += `${monthData.month} ${year}\n`;
            csvContent += '"Date","Category","Description","Amount"\n';
            
            monthData.expenses.forEach(expense => {
              const description = (expense.description || '').replace(/"/g, '""');
              csvContent += `"${new Date(expense.date).toLocaleDateString()}","${expense.category}","${description}","${formatCurrency(expense.amount)}"\n`;
            });
            
            csvContent += `"${monthData.month} Total Expenses:","${formatCurrency(monthData.totalExpenses)}"\n\n`;
          }
        });

        csvContent += `"ANNUAL TOTAL EXPENSES:","${formatCurrency(annualExpenses)}"\n\n`;

        // Section 3: Monthly Summary Table
        csvContent += '=== MONTHLY SUMMARY ===\n';
        csvContent += '"Month","Income","Expenses","Net Income"\n';
        
        monthlyData.forEach(monthData => {
          csvContent += `"${monthData.month}","${formatCurrency(monthData.totalIncome)}","${formatCurrency(monthData.totalExpenses)}","${formatCurrency(monthData.netIncome)}"\n`;
        });

        csvContent += '\n';

        // Section 4: Annual Summary
        csvContent += '=== ANNUAL P&L SUMMARY ===\n';
        csvContent += `"Total Annual Income:","${formatCurrency(annualIncome)}"\n`;
        csvContent += `"Total Annual Expenses:","${formatCurrency(annualExpenses)}"\n`;
        csvContent += `"Net Annual Income:","${formatCurrency(annualIncome - annualExpenses)}"\n`;
        csvContent += `"Total Deals Closed:","${allDeals.length}"\n`;
        csvContent += `"Total Expense Entries:","${allExpenses.length}"\n`;
      }

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Failed to export CSV:', error);
      setError('Failed to export data');
    }
  };

  // Calculate DD days remaining
  const calculateDDDaysRemaining = (ddStart, ddOver) => {
    if (!ddStart || !ddOver || ddStart === "" || ddOver === "") return { status: 'no-dates', text: 'No DD dates' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(ddStart);
    startDate.setHours(0, 0, 0, 0);
    
    const overDate = new Date(ddOver);
    overDate.setHours(0, 0, 0, 0);

    if (today < startDate) {
      const days = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
      return { status: 'upcoming', text: `Starts in ${days} ${days === 1 ? 'day' : 'days'}` };
    }

    if (today > overDate) {
      return { status: 'ended', text: '0 days' };
    }

    const days = Math.ceil((overDate - today) / (1000 * 60 * 60 * 24)) + 1;
    return { status: 'active', text: `${days} ${days === 1 ? 'day' : 'days'} left`, days };
  };

  // Generate month options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();

    // 15 total months: 3 future + current + 11 previous, newest first
    for (let i = 3; i >= -11; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  // Handle export to Excel
  const handleExport = async (period = 'month') => {
    try {
      const params = period === 'month' 
        ? { month: selectedMonth } 
        : { year: selectedMonth.split('-')[0] };
      
      const response = await axios.get(`${backendUrl}/api/pnl/export`, {
        params: { ...params, format: 'excel' },
        withCredentials: true
      });
      
      // This would handle the actual Excel download
      console.log('Export response:', response.data);
      alert('Export functionality will be available soon!');
    } catch (error) {
      console.error('Failed to export:', error);
      if (error.response?.status === 401) {
        setError('Authentication required');
      } else {
        setError('Failed to export data');
      }
    }
  };

  // Check if user has Pro access
  const hasProAccess = user?.plan === 'PRO';

  // Generate current month data for AI Coach
  const getCurrentMonthData = () => {
    if (!pnlSummary) return {};
    
    return {
      income: pnlSummary.total_income || 0,
      expenses: pnlSummary.total_expenses || 0,
      net: pnlSummary.net_income || 0,
      incomeCategories: extractIncomeCategories(pnlSummary.deals || []),
      expenseCategories: extractExpenseCategories(pnlSummary.expenses || [])
    };
  };

  // Generate past 6 months data for AI Coach trend analysis
  const getPastSixMonthsData = () => {
    const pastSixMonths = [];
    const currentDate = new Date();
    
    for (let i = 1; i <= 6; i++) {
      const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      
      // This would ideally fetch historical data from API
      // For now, we'll use placeholder data structure
      pastSixMonths.push({
        month: monthKey,
        income: 0, // Would be populated from API call
        expenses: 0,
        net: 0,
        incomeCategories: {},
        expenseCategories: {}
      });
    }
    
    return pastSixMonths;
  };

  // Helper function to extract income categories from deals
  const extractIncomeCategories = (deals) => {
    const categories = {};
    deals.forEach(deal => {
      const category = deal.lead_source || 'Other';
      categories[category] = (categories[category] || 0) + (deal.final_income || 0);
    });
    return categories;
  };

  // Helper function to extract expense categories from expenses
  const extractExpenseCategories = (expenses) => {
    const categories = {};
    expenses.forEach(expense => {
      const category = expense.category || 'Other';
      categories[category] = (categories[category] || 0) + (expense.amount || 0);
    });
    return categories;
  };

  // Load cap progress data
  const loadCapProgress = async () => {
    try {
      // Get auth token
      const token = await getToken();
      
      const response = await axios.get(`${backendUrl}/api/cap-tracker/progress`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setCapProgress(response.data);
    } catch (error) {
      // Cap progress is optional, don't show error if not configured
      console.log('Cap progress not available:', error);
    } finally {
      setCapLoading(false);
    }
  };

  // Cap Progress Component
  const CapProgressSection = () => {
    if (capLoading || !capProgress) return null;

    return (
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Commission Cap Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-gray-600">Progress</div>
              <div className="text-xl font-bold text-blue-600">{capProgress.percentage?.toFixed(1)}%</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Paid</div>
              <div className="text-xl font-bold text-green-600">{formatCurrency(capProgress.paid_so_far)}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Remaining</div>
              <div className={`text-xl font-bold ${capProgress.is_complete ? 'text-green-600' : 'text-orange-600'}`}>
                {formatCurrency(capProgress.remaining)}
              </div>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div 
              className={`h-4 rounded-full transition-all duration-300 ${
                capProgress.is_complete ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(capProgress.percentage, 100)}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between text-xs text-gray-600">
            <span>{formatCurrency(capProgress.paid_so_far)} / {formatCurrency(capProgress.total_cap)}</span>
            <span>{capProgress.deals_contributing} deals contributing</span>
          </div>

          {capProgress.is_complete && (
            <div className="mt-3 p-2 bg-green-100 text-green-800 text-sm rounded-lg text-center">
              🎉 Cap Complete! No more cap deductions from future deals.
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Inline editing functions
  const startEditing = (type, id, field, currentValue) => {
    setEditingCell({ type, id, field });
    setEditValue(currentValue.toString());
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;

    try {
      const { type, id, field } = editingCell;
      const newValue = parseFloat(editValue) || 0;

      if (type === 'deal') {
        // Update deal field
        await axios.patch(`${backendUrl}/api/pnl/deals/${id}`, {
          [field]: newValue
        }, {
          withCredentials: true
        });
      } else if (type === 'expense') {
        // Update expense field
        await axios.patch(`${backendUrl}/api/pnl/expenses/${id}`, {
          [field]: field === 'description' ? editValue : newValue
        }, {
          withCredentials: true
        });
      }

      // Reload data to show updated values
      await loadPnLData();
      
      // Clear editing state
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to update:', error);
      if (error.response?.status === 401) {
        setError('Authentication required');
      } else {
        setError('Failed to update value');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Agent P&L Tracker
            </h1>
            <p className="text-gray-600 mt-1">
              Track your real estate business income and expenses
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Professional P&L format: Income → Expenses → Net Income
            </p>
          </div>
          
          {hasProAccess && (
            <div className="flex space-x-2 mt-4 sm:mt-0">
              <Button
                onClick={() => setShowAICoach(true)}
                className="bg-green-600 hover:bg-green-700 text-white text-sm"
              >
                <Sparkles className="w-4 h-4 mr-2 text-white" />
                Fairy AI Coach
              </Button>
              {/* Hide export buttons on mobile */}
              {!isMobile && (
                <>
                  <Button
                    onClick={() => handleExportCSV('month')}
                    variant="outline"
                    className="text-sm"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export Month
                  </Button>
                  <Button
                    onClick={() => handleExportCSV('year')}
                    variant="outline"
                    className="text-sm"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export Year
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Pro Access Check */}
        {!hasProAccess && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <div>
                  <h3 className="font-semibold text-orange-900">Pro Feature Required</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    The P&L Tracker is a Pro-only feature. Upgrade your plan to track deals, expenses, and generate professional reports.
                  </p>
                  <Button 
                    className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => navigate('/pricing')}
                  >
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {hasProAccess && (
          <>
            {/* Month Selector */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div>
                    <Label htmlFor="month">Select Month</Label>
                    <select
                      id="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {generateMonthOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setShowAddDeal(true)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <HomeIcon className="w-4 h-4 mr-2" />
                      Add Deal
                    </Button>
                    <Button
                      onClick={() => setShowAddExpense(true)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Add Expense
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Active Deals Table - Hidden on mobile */}
            <div className="hidden sm:block">
              {filteredActiveDeals && filteredActiveDeals.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5" />
                      <span>Active Deals - Due Diligence Tracker</span>
                      <Badge variant="secondary">{filteredActiveDeals.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Desktop: Table View */}
                    {!isMobile && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="text-left text-sm text-gray-500 border-b">
                            <th className="pb-2">Property Address</th>
                            <th className="pb-2">Contract Signed</th>
                            <th className="pb-2">DD Start</th>
                            <th className="pb-2">DD Over</th>
                            <th className="pb-2">DD Status</th>
                            <th className="pb-2">Closing Date</th>
                            <th className="pb-2">Final Income</th>
                            <th className="pb-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredActiveDeals.map((deal) => {
                            const ddInfo = calculateDDDaysRemaining(deal.due_diligence_start, deal.due_diligence_over);
                            return (
                              <tr 
                                key={deal.id} 
                                className="border-b hover:bg-blue-50 transition-colors"
                              >
                                <td 
                                  className="py-3 text-sm font-medium cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {deal.house_address}
                                </td>
                                <td 
                                  className="py-3 text-sm cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {deal.contract_signed ? new Date(deal.contract_signed).toLocaleDateString() : '—'}
                                </td>
                                <td 
                                  className="py-3 text-sm cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {deal.due_diligence_start ? new Date(deal.due_diligence_start).toLocaleDateString() : '—'}
                                </td>
                                <td 
                                  className="py-3 text-sm cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {deal.due_diligence_over ? new Date(deal.due_diligence_over).toLocaleDateString() : '—'}
                                </td>
                                <td 
                                  className="py-3 cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                                    ddInfo.status === 'no-dates' ? 'bg-gray-100 text-gray-600' :
                                    ddInfo.status === 'ended' ? 'bg-red-50 text-red-600' :
                                    ddInfo.status === 'upcoming' ? 'bg-blue-50 text-blue-600' :
                                    ddInfo.days <= 3 ? 'bg-orange-50 text-orange-600' :
                                    'bg-green-50 text-green-600'
                                  }`}>
                                    {ddInfo.text}
                                  </span>
                                </td>
                                <td 
                                  className="py-3 text-sm cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {new Date(deal.closing_date).toLocaleDateString()}
                                </td>
                                <td 
                                  className="py-3 text-sm font-bold text-green-600 cursor-pointer"
                                  onClick={() => handleEditDeal(deal)}
                                >
                                  {formatCurrency(deal.final_income)}
                                </td>
                                <td className="py-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Are you sure you want to delete the deal for ${deal.house_address}?`)) {
                                        deleteDeal(deal.id);
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Mobile: Card View */}
                  {isMobile && (
                    <div className="space-y-3">
                      {filteredActiveDeals.map((deal) => {
                        const ddInfo = calculateDDDaysRemaining(deal.due_diligence_start, deal.due_diligence_over);
                        return (
                          <div
                            key={deal.id}
                            className="border rounded-lg p-3 bg-gray-50"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div 
                                className="flex-1 cursor-pointer"
                                onClick={() => handleEditDeal(deal)}
                              >
                                <h4 className="font-semibold text-sm text-gray-900">
                                  {deal.house_address}
                                </h4>
                                <p className="text-xs text-gray-600 mt-1">
                                  Closing: {new Date(deal.closing_date).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete deal for ${deal.house_address}?`)) {
                                    deleteDeal(deal.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div 
                              className="flex items-center justify-between cursor-pointer"
                              onClick={() => handleEditDeal(deal)}
                            >
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-600">DD:</span>
                                <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                                  ddInfo.status === 'no-dates' ? 'bg-gray-100 text-gray-600' :
                                  ddInfo.status === 'ended' ? 'bg-red-50 text-red-600' :
                                  ddInfo.status === 'upcoming' ? 'bg-blue-50 text-blue-600' :
                                  ddInfo.days <= 3 ? 'bg-orange-50 text-orange-600' :
                                  'bg-green-50 text-green-600'
                                }`}>
                                  {ddInfo.text}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-green-600">
                                {formatCurrency(deal.final_income)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </div>

            {/* Error Display */}
            {error && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setError(null)}
                      className="ml-auto"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="pt-6">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-32"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : pnlSummary ? (
              <>
                {/* P&L Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-600">Total Income</div>
                          <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(pnlSummary.total_income)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            From {pnlSummary.deals.length} deal{pnlSummary.deals.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-600">Total Expenses</div>
                          <div className="text-2xl font-bold text-red-600">
                            {formatCurrency(pnlSummary.total_expenses)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            From {pnlSummary.expenses.length} expense{pnlSummary.expenses.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <TrendingDown className="w-8 h-8 text-red-600" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-600">Net Income</div>
                          <div className={`text-2xl font-bold ${
                            pnlSummary.net_income >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(pnlSummary.net_income)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {pnlSummary.net_income >= 0 ? 'Profit' : 'Loss'} for the month
                          </div>
                        </div>
                        <DollarSign className={`w-8 h-8 ${
                          pnlSummary.net_income >= 0 ? 'text-green-600' : 'text-red-600'
                        }`} />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Cap Progress Section - Hidden on mobile */}
                <div className="hidden sm:block">
                  <CapProgressSection />
                </div>

                {/* Income Section (Deals) */}
                <Card>
                  {/* Mobile: Full-width header */}
                  <div className="md:hidden bg-green-50 border-b border-green-100 px-4 py-3">
                    <div className="flex items-center">
                      <TrendingUp className="w-5 h-5 text-green-700 mr-2" />
                      <h3 className="text-lg font-semibold text-green-700">Income</h3>
                    </div>
                  </div>
                  
                  {/* Desktop: Original header */}
                  <CardHeader className="hidden md:block">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-green-700">
                        <TrendingUp className="w-5 h-5 inline mr-2" />
                        Income
                      </CardTitle>
                      <div className="text-sm text-gray-500">
                        Calculate final income: Agent's Gross Commission × (100% - Team/Brokerage Split %)
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {pnlSummary.deals.length > 0 ? (
                      <>
                        {/* Desktop: Table view */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="text-left text-sm text-gray-500 border-b">
                                <th className="pb-2">Property Address</th>
                                <th className="pb-2">Sale Price</th>
                                <th className="pb-2">Commission %</th>
                                <th className="pb-2">Split %</th>
                                <th className="pb-2">Team/Brokerage Split %</th>
                                <th className="pb-2">Cap Amount</th>
                                <th className="pb-2">Final Income</th>
                                <th className="pb-2">Lead Source</th>
                                <th className="pb-2">Closing Date</th>
                                <th className="pb-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                            {pnlSummary.deals.map((deal) => (
                              <tr key={deal.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 text-sm font-medium">{deal.house_address}</td>
                                
                                {/* Sale Price - Editable */}
                                <td className="py-2 text-sm">
                                  {editingCell?.type === 'deal' && editingCell?.id === deal.id && editingCell?.field === 'amount_sold_for' ? (
                                    <div className="flex items-center space-x-1">
                                      <Input
                                        type="number"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        className="w-24 h-7 text-xs"
                                        autoFocus
                                      />
                                      <button
                                        onClick={saveEdit}
                                        className="text-green-600 hover:text-green-800"
                                        title="Save"
                                      >
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="text-red-600 hover:text-red-800"
                                        title="Cancel"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => startEditing('deal', deal.id, 'amount_sold_for', deal.amount_sold_for)}
                                      className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                      title="Click to edit"
                                    >
                                      <span>{formatCurrency(deal.amount_sold_for)}</span>
                                      <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                    </button>
                                  )}
                                </td>
                                
                                {/* Commission % - Editable */}
                                <td className="py-2 text-sm">
                                  {editingCell?.type === 'deal' && editingCell?.id === deal.id && editingCell?.field === 'commission_percent' ? (
                                    <div className="flex items-center space-x-1">
                                      <Input
                                        type="number"
                                        step="0.1"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        className="w-16 h-7 text-xs"
                                        autoFocus
                                      />
                                      <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button onClick={cancelEditing} className="text-red-600 hover:text-red-800" title="Cancel">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => startEditing('deal', deal.id, 'commission_percent', deal.commission_percent)}
                                      className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                      title="Click to edit"
                                    >
                                      <span>{deal.commission_percent}%</span>
                                      <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                    </button>
                                  )}
                                </td>
                                
                                {/* Split % - Editable */}
                                <td className="py-2 text-sm">
                                  {editingCell?.type === 'deal' && editingCell?.id === deal.id && editingCell?.field === 'split_percent' ? (
                                    <div className="flex items-center space-x-1">
                                      <Input
                                        type="number"
                                        step="0.1"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        className="w-16 h-7 text-xs"
                                        autoFocus
                                      />
                                      <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button onClick={cancelEditing} className="text-red-600 hover:text-red-800" title="Cancel">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => startEditing('deal', deal.id, 'split_percent', deal.split_percent)}
                                      className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                      title="Click to edit"
                                    >
                                      <span>{deal.split_percent}%</span>
                                      <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                    </button>
                                  )}
                                </td>
                                
                                {/* Team/Brokerage Split % - Editable */}
                                <td className="py-2 text-sm">
                                  {editingCell?.type === 'deal' && editingCell?.id === deal.id && editingCell?.field === 'team_brokerage_split_percent' ? (
                                    <div className="flex items-center space-x-1">
                                      <Input
                                        type="number"
                                        step="0.1"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        className="w-16 h-7 text-xs"
                                        autoFocus
                                      />
                                      <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button onClick={cancelEditing} className="text-red-600 hover:text-red-800" title="Cancel">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => startEditing('deal', deal.id, 'team_brokerage_split_percent', deal.team_brokerage_split_percent)}
                                      className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                      title="Click to edit"
                                    >
                                      <span>{deal.team_brokerage_split_percent}%</span>
                                      <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                    </button>
                                  )}
                                </td>
                                
                                {/* Cap Amount - Display only */}
                                <td className="py-2 text-sm font-medium text-orange-600">
                                  {deal.cap_amount ? formatCurrency(deal.cap_amount) : '—'}
                                </td>
                                
                                {/* Final Income - Calculated, not editable */}
                                <td className="py-2 text-sm font-bold text-green-600">
                                  {formatCurrency(deal.final_income)}
                                </td>
                                
                                <td className="py-2 text-sm">{deal.lead_source}</td>
                                <td className="py-2 text-sm">{new Date(deal.closing_date).toLocaleDateString()}</td>
                                <td className="py-2">
                                  <div className="flex space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEditDeal(deal)}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deleteDeal(deal.id)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Mobile: Card view */}
                      <div className="md:hidden space-y-4">
                        {pnlSummary.deals.map((deal) => (
                          <div key={deal.id} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                            <div className="flex justify-between items-start">
                              <div className="font-medium text-gray-900 flex-1 pr-2">
                                {deal.house_address}
                              </div>
                              <button
                                onClick={() => deleteDeal(deal.id)}
                                className="text-red-600 hover:text-red-800 flex-shrink-0"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500 text-xs">Sale Price:</span>
                                <div className="font-semibold">{formatCurrency(deal.amount_sold_for || 0)}</div>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs">Final Income:</span>
                                <div className="font-semibold text-green-700">{formatCurrency(deal.final_income || 0)}</div>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs">Commission %:</span>
                                <div className="font-medium">{deal.commission_percent || 0}%</div>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs">Your Split %:</span>
                                <div className="font-medium">{deal.split_percent || 0}%</div>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs">Team/Brokerage:</span>
                                <div className="font-medium">{deal.team_brokerage_split_percent || 0}%</div>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs">Cap Amount:</span>
                                <div className="font-medium">{formatCurrency(deal.cap_amount || 0)}</div>
                              </div>
                            </div>
                            
                            <div className="pt-2 border-t border-gray-200 text-xs text-gray-600">
                              <div className="flex justify-between">
                                <span>Lead Source: {deal.lead_source || 'N/A'}</span>
                                <span>Closing: {new Date(deal.closing_date + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Building className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No deals recorded for this month</p>
                        <Button 
                          onClick={() => setShowAddDeal(true)}
                          className="mt-3 bg-green-600 hover:bg-green-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Your First Deal
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Expenses Section */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-red-700">
                        <TrendingDown className="w-5 h-5 inline mr-2" />
                        Expenses - Business Costs
                      </CardTitle>
                      <div className="text-sm text-gray-500">
                        Track all business-related expenses with budget utilization
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {pnlSummary.expenses.length > 0 ? (
                      <>
                        {/* Desktop: Table view */}
                        <div className="hidden lg:block overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="text-left text-sm text-gray-500 border-b">
                                <th className="pb-2">Date</th>
                                <th className="pb-2">Category</th>
                                <th className="pb-2">Description</th>
                                <th className="pb-2">Amount</th>
                                <th className="pb-2">Budget Utilization</th>
                                <th className="pb-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pnlSummary.expenses.map((expense) => {
                                const utilization = pnlSummary.budget_utilization[expense.category];
                                return (
                                  <tr key={expense.id} className="border-b hover:bg-gray-50">
                                    <td className="py-2 text-sm">{new Date(expense.date).toLocaleDateString()}</td>
                                    <td className="py-2 text-sm font-medium">
                                      {expense.category}
                                      {expense.recurring && (
                                        <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                          Recurring
                                        </span>
                                      )}
                                      {expense.is_recurring_instance && (
                                        <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                                          Auto
                                        </span>
                                      )}
                                    </td>
                                    
                                    {/* Description - Editable */}
                                    <td className="py-2 text-sm">
                                      {editingCell?.type === 'expense' && editingCell?.id === expense.id && editingCell?.field === 'description' ? (
                                        <div className="flex items-center space-x-1">
                                          <Input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            className="w-32 h-7 text-xs"
                                            autoFocus
                                          />
                                          <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                            <Check className="w-3 h-3" />
                                          </button>
                                          <button onClick={cancelEditing} className="text-red-600 hover:text-red-800" title="Cancel">
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => startEditing('expense', expense.id, 'description', expense.description || '')}
                                          className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                          title="Click to edit"
                                        >
                                          <span>{expense.description || '—'}</span>
                                          <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                        </button>
                                      )}
                                    </td>
                                    
                                    {/* Amount - Editable */}
                                    <td className="py-2 text-sm font-bold text-red-600">
                                      {editingCell?.type === 'expense' && editingCell?.id === expense.id && editingCell?.field === 'amount' ? (
                                        <div className="flex items-center space-x-1">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            className="w-20 h-7 text-xs"
                                            autoFocus
                                          />
                                          <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                            <Check className="w-3 h-3" />
                                          </button>
                                          <button onClick={cancelEditing} className="text-red-600 hover:text-red-800" title="Cancel">
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => startEditing('expense', expense.id, 'amount', expense.amount)}
                                          className="hover:bg-blue-100 px-2 py-1 rounded group flex items-center"
                                          title="Click to edit"
                                        >
                                          <span>{formatCurrency(expense.amount)}</span>
                                          <Edit3 className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-50" />
                                        </button>
                                      )}
                                    </td>
                                    <td className="py-2 text-sm">
                                      {utilization ? (
                                        <div className="flex items-center space-x-2">
                                          <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div 
                                              className={`h-2 rounded-full ${
                                                utilization.percent > 100 ? 'bg-red-500' : 
                                                utilization.percent > 80 ? 'bg-yellow-500' : 'bg-green-500'
                                              }`}
                                              style={{ width: `${Math.min(utilization.percent, 100)}%` }}
                                            ></div>
                                          </div>
                                          <span className="text-xs">
                                            {utilization.percent.toFixed(0)}% of {formatCurrency(utilization.budget)}
                                          </span>
                                        </div>
                                      ) : '—'}
                                    </td>
                                    <td className="py-2">
                                      <div className="flex space-x-1">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleEditExpense(expense)}
                                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                          <Edit3 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => deleteExpense(expense.id)}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Mobile: Card view */}
                        <div className="lg:hidden space-y-3">
                          {pnlSummary.expenses.map((expense) => {
                            const utilization = pnlSummary.budget_utilization[expense.category];
                            return (
                              <div key={expense.id} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {expense.category}
                                      {expense.is_recurring_instance && (
                                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                          Auto
                                        </span>
                                      )}
                                      {expense.recurring && (
                                        <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                          Recurring
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-500 mt-1">
                                      {expense.description || 'No description'}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1 ml-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEditExpense(expense)}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deleteExpense(expense.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-500">Date:</span>
                                    <span className="ml-2 font-medium">{new Date(expense.date).toLocaleDateString()}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Amount:</span>
                                    <span className="ml-2 font-bold text-red-600">{formatCurrency(expense.amount)}</span>
                                  </div>
                                </div>
                                
                                {utilization && utilization.budget > 0 && (
                                  <div className="pt-2 border-t">
                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                      <span>Budget Utilization</span>
                                      <span>{utilization.percent.toFixed(0)}% of {formatCurrency(utilization.budget)}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className={`h-2 rounded-full ${
                                          utilization.percent > 100 ? 'bg-red-500' : 
                                          utilization.percent > 80 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(utilization.percent, 100)}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No expenses recorded for this month</p>
                        <Button 
                          onClick={() => setShowAddExpense(true)}
                          className="mt-3 bg-red-600 hover:bg-red-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Your First Expense
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Budget Utilization Summary */}
                {Object.keys(pnlSummary.budget_utilization).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget Utilization Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(pnlSummary.budget_utilization).map(([category, util]) => {
                          const hasBudget = util.budget > 0;
                          const isOverBudget = hasBudget && util.spent > util.budget;
                          
                          return (
                            <div key={category} className="border rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-2">{category}</h4>
                              <div className="space-y-2">
                                {hasBudget ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span>Budget:</span>
                                      <span className="font-semibold">{formatCurrency(util.budget)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span>Spent:</span>
                                      <span className="font-semibold text-red-600">{formatCurrency(util.spent)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span>{isOverBudget ? 'Over budget by:' : 'Remaining:'}</span>
                                      <span className={`font-semibold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
                                        {isOverBudget ? formatCurrency(Math.abs(util.remaining)) : formatCurrency(util.remaining)}
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                      <div 
                                        className={`h-2 rounded-full ${
                                          util.percent > 100 ? 'bg-red-500' : 
                                          util.percent > 80 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(util.percent, 100)}%` }}
                                      ></div>
                                    </div>
                                    <div className={`text-xs text-center ${util.percent > 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {util.percent.toFixed(1)}% utilized
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span>Budget:</span>
                                      <span className="text-gray-400 italic">No budget set</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span>Spent:</span>
                                      <span className="font-semibold text-red-600">{formatCurrency(util.spent)}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                      <div className="h-2 rounded-full bg-gray-300" style={{ width: '0%' }}></div>
                                    </div>
                                    <div className="text-xs text-center text-gray-400 italic">
                                      No budget set
                                    </div>
                                    {editingBudget === category ? (
                                      <div className="flex items-center space-x-2 mt-2">
                                        <Input
                                          type="number"
                                          placeholder="Enter budget"
                                          value={budgetInputValue}
                                          onChange={(e) => setBudgetInputValue(e.target.value)}
                                          className="h-8 text-sm"
                                        />
                                        <Button
                                          size="sm"
                                          onClick={() => handleSetBudget(category)}
                                          className="h-8 bg-green-600 hover:bg-green-700 text-white"
                                        >
                                          <Check className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingBudget(null);
                                            setBudgetInputValue('');
                                          }}
                                          className="h-8"
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setEditingBudget(category);
                                          setBudgetInputValue('');
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                                      >
                                        Set Budget
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </>
        )}

        {/* Add Deal Modal */}
        {showAddDeal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
              <CardHeader>
                <CardTitle>Add New Deal</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddDeal} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="house_address">Property Address *</Label>
                      <Input
                        id="house_address"
                        value={newDeal.house_address}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, house_address: e.target.value }))}
                        placeholder="123 Main St, City, State"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="amount_sold_for">Amount Sold For *</Label>
                      <Input
                        id="amount_sold_for"
                        type="text"
                        value={newDeal.amount_sold_for}
                        onChange={handleDealAmountChange}
                        placeholder="500,000"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="commission_percent">Commission % *</Label>
                      <Input
                        id="commission_percent"
                        type="number"
                        step="0.1"
                        value={newDeal.commission_percent}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, commission_percent: e.target.value }))}
                        placeholder="6.0"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="split_percent">Agent's Share % (Default: 100%)</Label>
                      <Input
                        id="split_percent"
                        type="number"
                        step="0.1"
                        value={newDeal.split_percent}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, split_percent: e.target.value }))}
                        placeholder="100.0"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        What % of total commission you receive (leave blank for 100%)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="team_brokerage_split_percent">Team/Brokerage Split % *</Label>
                      <Input
                        id="team_brokerage_split_percent"
                        type="number"
                        step="0.1"
                        value={newDeal.team_brokerage_split_percent}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, team_brokerage_split_percent: e.target.value }))}
                        placeholder="20.0"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="lead_source">Lead Source *</Label>
                      <select
                        id="lead_source"
                        value={newDeal.lead_source}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, lead_source: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select lead source</option>
                        {leadSources.map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="contract_signed">Contract Signed *</Label>
                      <Input
                        id="contract_signed"
                        type="date"
                        value={newDeal.contract_signed}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, contract_signed: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="due_diligence_start">Due Diligence Start *</Label>
                      <Input
                        id="due_diligence_start"
                        type="date"
                        value={newDeal.due_diligence_start}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, due_diligence_start: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="due_diligence_over">Due Diligence Over *</Label>
                      <Input
                        id="due_diligence_over"
                        type="date"
                        value={newDeal.due_diligence_over}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, due_diligence_over: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label htmlFor="closing_date">Closing Date *</Label>
                      <Input
                        id="closing_date"
                        type="date"
                        value={newDeal.closing_date}
                        onChange={(e) => setNewDeal(prev => ({ ...prev, closing_date: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 pt-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      Add Deal
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddDeal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Deal Modal */}
        {showEditDeal && editingDeal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
              <CardHeader>
                <CardTitle>Edit Deal</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateDeal} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_house_address">Property Address *</Label>
                      <Input
                        id="edit_house_address"
                        value={editingDeal.house_address}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, house_address: e.target.value }))}
                        placeholder="123 Main St, City, State"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_amount_sold_for">Amount Sold For *</Label>
                      <Input
                        id="edit_amount_sold_for"
                        type="number"
                        value={editingDeal.amount_sold_for}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, amount_sold_for: e.target.value }))}
                        placeholder="500000"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_commission_percent">Commission % *</Label>
                      <Input
                        id="edit_commission_percent"
                        type="number"
                        step="0.1"
                        value={editingDeal.commission_percent}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, commission_percent: e.target.value }))}
                        placeholder="6.0"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_split_percent">Agent's Share % (Default: 100%)</Label>
                      <Input
                        id="edit_split_percent"
                        type="number"
                        step="0.1"
                        value={editingDeal.split_percent}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, split_percent: e.target.value }))}
                        placeholder="100.0"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        What % of total commission you receive (leave blank for 100%)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_team_brokerage_split_percent">Team/Brokerage Split % *</Label>
                      <Input
                        id="edit_team_brokerage_split_percent"
                        type="number"
                        step="0.1"
                        value={editingDeal.team_brokerage_split_percent}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, team_brokerage_split_percent: e.target.value }))}
                        placeholder="20.0"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_lead_source">Lead Source *</Label>
                      <select
                        id="edit_lead_source"
                        value={editingDeal.lead_source}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, lead_source: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select lead source</option>
                        {leadSources.map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_contract_signed">Contract Signed</Label>
                      <Input
                        id="edit_contract_signed"
                        type="date"
                        value={editingDeal.contract_signed}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, contract_signed: e.target.value }))}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="edit_due_diligence_start">Due Diligence Start</Label>
                      <Input
                        id="edit_due_diligence_start"
                        type="date"
                        value={editingDeal.due_diligence_start}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, due_diligence_start: e.target.value }))}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="edit_due_diligence_over">Due Diligence Over</Label>
                      <Input
                        id="edit_due_diligence_over"
                        type="date"
                        value={editingDeal.due_diligence_over}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, due_diligence_over: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label htmlFor="edit_closing_date">Closing Date *</Label>
                      <Input
                        id="edit_closing_date"
                        type="date"
                        value={editingDeal.closing_date}
                        onChange={(e) => setEditingDeal(prev => ({ ...prev, closing_date: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 pt-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Update Deal
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditDeal(false);
                        setEditingDeal(null);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete the deal for ${editingDeal.house_address}?`)) {
                          deleteDeal(editingDeal.id);
                          setShowEditDeal(false);
                          setEditingDeal(null);
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Add Expense Modal */}
        {showAddExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
              <CardHeader>
                <CardTitle>Add New Expense</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddExpense} className="space-y-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newExpense.date}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      value={newExpense.category}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="">Select category</option>
                      {expenseCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="100.00"
                      className="mt-1"
                      required
                    />
                  </div>

                  {/* Recurring Expense Checkbox */}
                  <div className="flex items-start space-x-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="recurring"
                      checked={newExpense.recurring}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, recurring: e.target.checked }))}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <Label htmlFor="recurring" className="font-medium text-blue-900 text-base">
                        ✨ Make this expense recurring
                      </Label>
                      <p className="text-sm text-blue-700 mt-1">
                        This expense will automatically appear every month through December {new Date().getFullYear()}. 
                        You'll need to re-check this for expenses starting January 1st of next year.
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="budget">Budget (Optional)</Label>
                    <Input
                      id="budget"
                      type="number"
                      step="0.01"
                      value={newExpense.budget}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="1000.00"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={newExpense.description}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description..."
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Duplicate removed - now above */}
                  
                  <div className="flex space-x-2 pt-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Add Expense
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddExpense(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Edit Expense Modal */}
      {showEditExpense && editingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
            <CardHeader>
              <CardTitle>Edit Expense</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateExpense} className="space-y-4">
                <div>
                  <Label htmlFor="edit_expense_date">Date *</Label>
                  <Input
                    id="edit_expense_date"
                    type="date"
                    value={editingExpense.date}
                    onChange={(e) => setEditingExpense(prev => ({ ...prev, date: e.target.value }))}
                    className="mt-1"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_expense_category">Category *</Label>
                  <select
                    id="edit_expense_category"
                    value={editingExpense.category}
                    onChange={(e) => setEditingExpense(prev => ({ ...prev, category: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Select category</option>
                    {expenseCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="edit_expense_description">Description</Label>
                  <Input
                    id="edit_expense_description"
                    value={editingExpense.description}
                    onChange={(e) => setEditingExpense(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description..."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_expense_amount">Amount *</Label>
                  <Input
                    id="edit_expense_amount"
                    type="number"
                    step="0.01"
                    value={editingExpense.amount}
                    onChange={(e) => setEditingExpense(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="100.00"
                    className="mt-1"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_expense_budget">Budget for this Category</Label>
                  <Input
                    id="edit_expense_budget"
                    type="number"
                    step="0.01"
                    value={editingExpense.budget}
                    onChange={(e) => setEditingExpense(prev => ({ ...prev, budget: e.target.value }))}
                    placeholder="1000.00"
                    className="mt-1"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditExpense(false);
                      setEditingExpense(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* AI Coach Modal */}
      <PnLAICoach 
        isOpen={showAICoach}
        onClose={() => setShowAICoach(false)}
        currentMonthData={getCurrentMonthData()}
        pastSixMonthsData={getPastSixMonthsData()}
      />

    
    </div>
  );
};

export default PnLPanel;